import Phaser from "phaser";
import {
  ModalLayer,
  NineSlicePanel,
  NineSliceButton,
  NINE_SLICE_BUTTON_EVENT,
} from "@yjsgames/phaser-ui";
import {
  CONTENT_PANEL_STYLE,
  FONT_FAMILY,
  hexToNum,
  PRIMARY_BUTTON_STYLE,
  RARITY_COLOR,
  rarityLabel,
  setButtonEnabled,
  TYPO,
} from "./uiTokens.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { drawRewardShapeIcon } from "./rewardShapeIcon.js";
import { FALLBACK_PIECE, type ModulePiece } from "../data/modulePieces.js";
import { t, getTextDirection } from "../i18n/i18n.js";
import { popupCloseButtonX } from "./directionalUi.js";
import { setExpandedCloseButtonHitArea } from "./closeButtonHitArea.js";
import type { ModuleInfoData } from "./tabData.js";
import { moduleCodexIconKey } from "./modulePresentationIcon.js";
import { MODULE_BRICK_BALANCE } from "../data/balance.js";

/** 정보 팝업 하단 액션 버튼(합성 탭 등록 버튼 등) — 정보 전용으로 열 땐 생략. */
export interface ModuleInfoAction {
  readonly label: string;
  readonly enabled: boolean;
  readonly onAction: () => void;
  /** 라벨 왼쪽 아이콘 텍스처 키 (예: 강화석). 미지정 시 라벨만. */
  readonly iconKey?: string;
  /** 아이콘 크기(px). 미지정 시 버튼 기본. */
  readonly iconSize?: number;
}

export interface ModuleInfoPopupOptions {
  readonly screenWidth: number;
  readonly screenHeight: number;
  readonly onClose: () => void;
  /** 하단 액션 버튼(예: 합성 재료 등록 · 강화). 없으면 정보 전용. unequipAction과 둘 다면 좌우 분할. */
  readonly action?: ModuleInfoAction;
  /** 하단 "장착 해제" 버튼(전투 그리드 탭 배치 인스턴스). action과 공존 가능 — 배치 파이프는 강화+해제 2버튼. */
  readonly unequipAction?: ModuleInfoAction;
}

const PANEL_W = 320;
const PANEL_H = 384;
/** 하단 액션 버튼이 있을 때 패널에 추가하는 버튼 영역 높이. */
const ACTION_AREA_H = 52;
/**
 * 메인 모양 미리보기 셀 크기(px). 세로 2칸(vert2) footprint도 인접 텍스트(등급 라벨·모양 풀 라벨)와
 * 겹치지 않도록 셀을 키우고 위아래 여백을 충분히 확보한다(레이아웃 좌표 rarity↔poolLabel 간격).
 */
const MAIN_CELL_PX = 46;
const POOL_CELL_PX = 30;
const POOL_GAP = 14;


/**
 * 모듈 정보 팝업 — ModalLayer 기반 (RelicDetailPopup 패턴). 합성 결과 + 전투 그리드 탭 공용.
 * 이름·등급·레벨 + 기능 대표 아이콘 + 모양 풀 + 기능 설명 + 구체 스탯(현재 등급·레벨 평가).
 * 합성 탭에서 열 때만 하단 등록 버튼(opts.action)을 표시한다 — dim/닫기(✕)는 onClose로 닫는다.
 * 데이터는 buildModuleInfoData(순수)가 만들고 update()로 주입한다.
 */
export class ModuleInfoPopup extends ModalLayer {
  private readonly opts: ModuleInfoPopupOptions;
  private readonly cx: number;
  private readonly title: Phaser.GameObjects.Text;
  private readonly rarityText: Phaser.GameObjects.Text;
  private readonly chanceText: Phaser.GameObjects.Text;
  private readonly mainIcon: Phaser.GameObjects.Image;
  private readonly poolLabel: Phaser.GameObjects.Text;
  private readonly descText: Phaser.GameObjects.Text;
  /** 구체 스탯 라인(발사체 수 ×2 등) — 행별 색 강조를 위해 개별 Text로 렌더한다. */
  private readonly statTexts: Phaser.GameObjects.Text[] = [];
  /** 모양 풀 행(가변 2~3개) — update마다 재구성. */
  private readonly poolEntries: Phaser.GameObjects.GameObject[] = [];
  private readonly poolRowY: number;
  /** 메인 미리보기에 확대 표시 중인 조각(모양+footprint) — renderVariant에서 보유(대표) 조각으로만 세팅(풀 클릭 전환 폐기). */
  private activePiece: ModulePiece = FALLBACK_PIECE;
  /** 등급 미리보기 variants(낮음→높음) + 현재 보기 인덱스(◀/▶로 이동). 단일 등급이면 길이 1(버튼 숨김). */
  private variants: readonly ModuleInfoData[] = [];
  private viewIndex = 0;
  /** 등급 미리보기 이전/다음 버튼(◀/▶) — 등급 라인 양옆. */
  private readonly prevBtn: Phaser.GameObjects.Text;
  private readonly nextBtn: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene, opts: ModuleInfoPopupOptions) {
    super(scene, {
      screenWidth: opts.screenWidth,
      screenHeight: opts.screenHeight,
      overlayColor: PALETTE_NUM.overlayDim,
      overlayAlpha: 0.82,
      onBackdropClick: () => opts.onClose(),
    });
    this.opts = opts;

    const cx = opts.screenWidth / 2;
    const cy = opts.screenHeight / 2;
    // 하단 액션 버튼(강화/등록/장착 해제)이 하나라도 있으면 버튼 영역만큼 패널을 키운다. 정보 전용은 기존 높이.
    const ph = opts.action || opts.unequipAction ? PANEL_H + ACTION_AREA_H : PANEL_H;
    const top = cy - ph / 2;
    this.cx = cx;
    // dim 클릭 닫기 — 패널 영역은 제외(패널 내부 클릭은 안 닫힘).
    this.setBackdropCloseExclusion(cx - PANEL_W / 2, top, PANEL_W, ph);

    const panel = new NineSlicePanel(scene, cx, cy, {
      style: CONTENT_PANEL_STYLE,
      width: PANEL_W,
      height: ph,
      originX: 0.5,
      originY: 0.5,
    });
    this.contentContainer.add(panel);

    this.title = scene.add
      .text(cx, top + 24, "", { fontFamily: FONT_FAMILY, fontSize: TYPO.title, color: PALETTE.textBright })
      .setOrigin(0.5);
    const closeBtn = scene.add
      .text(popupCloseButtonX(cx, PANEL_W, 18, getTextDirection()), top + 16, "✕", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.display,
        color: PALETTE.textSecondary,
      })
      .setOrigin(0.5);
    setExpandedCloseButtonHitArea(closeBtn);
    closeBtn.on("pointerup", () => this.opts.onClose());

    this.rarityText = scene.add
      .text(cx, top + 48, "", { fontFamily: FONT_FAMILY, fontSize: TYPO.body, color: PALETTE.textDim })
      .setOrigin(0.5);

    // 등급 미리보기 ◀/▶ — 등급 라인 양옆. variants 2개+일 때만 표시(updateRarityNav). 클릭으로 인접 등급 효과 미리보기.
    this.prevBtn = scene.add
      .text(cx - 92, top + 48, "◀", { fontFamily: FONT_FAMILY, fontSize: TYPO.body, color: PALETTE.textSecondary })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    this.prevBtn.on("pointerup", () => this.stepRarity(-1));
    this.nextBtn = scene.add
      .text(cx + 92, top + 48, "▶", { fontFamily: FONT_FAMILY, fontSize: TYPO.body, color: PALETTE.textSecondary })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    this.nextBtn.on("pointerup", () => this.stepRarity(1));

    // 발동 확률 라인 — 기능(설명) 영역 바로 위에 배치(top+260). chance 게이트가 있는 등급에서만 표시(없으면 setVisible(false)).
    this.chanceText = scene.add
      .text(cx, top + 260, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.caption,
        color: PALETTE.accentAmber,
      })
      .setOrigin(0.5);

    this.mainIcon = scene.add
      .image(cx, top + 108, "icon_tab_codex")
      .setDisplaySize(MAIN_CELL_PX, MAIN_CELL_PX);

    this.poolLabel = scene.add
      .text(cx, top + 190, t("moduleInfo.shapePoolLabel"), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.caption,
        color: PALETTE.textSecondary,
      })
      .setOrigin(0.5);
    this.poolRowY = top + 222;

    this.descText = scene.add
      .text(cx, top + 282, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.body,
        color: PALETTE.positiveSoft,
        align: "center",
        wordWrap: { width: PANEL_W - 44 },
      })
      .setOrigin(0.5, 0);
    this.contentContainer.add([
      this.title,
      closeBtn,
      this.rarityText,
      this.prevBtn,
      this.nextBtn,
      this.chanceText,
      this.mainIcon,
      this.poolLabel,
      this.descText,
    ]);

    // 하단 액션 버튼 — action(합성 등록 · 강화) + unequipAction(장착 해제). 둘 다면 좌우 분할(146px),
    // 하나만이면 중앙(160px). 클릭 시 각 action.onAction(GameScene이 토글/제거 후 팝업 닫음).
    const btnY = top + ph - 28;
    const both = !!opts.action && !!opts.unequipAction;
    const mkActionBtn = (a: ModuleInfoAction, bx: number, bw: number): NineSliceButton => {
      const btn = new NineSliceButton(scene, bx, btnY, {
        style: PRIMARY_BUTTON_STYLE,
        label: a.label,
        widthOverride: bw,
        ...(a.iconKey ? { iconKey: a.iconKey, iconSize: a.iconSize ?? 14 } : {}),
      });
      btn.on(NINE_SLICE_BUTTON_EVENT.Click, () => a.onAction());
      setButtonEnabled(btn, a.enabled);
      this.contentContainer.add(btn);
      return btn;
    };
    if (both) {
      mkActionBtn(opts.action!, cx - 77, 146);
      mkActionBtn(opts.unequipAction!, cx + 77, 146);
    } else if (opts.action) {
      mkActionBtn(opts.action, cx, 160);
    } else if (opts.unequipAction) {
      mkActionBtn(opts.unequipAction, cx, 160);
    }
  }

  /** 표시 갱신 (열 때 + 다른 모듈로 교체 시). rarityNav가 있으면 등급 미리보기 ◀/▶를 켜고 보유 등급에서 시작. */
  update(data: ModuleInfoData): void {
    if (data.rarityNav) {
      this.variants = data.rarityNav.variants;
      this.viewIndex = data.rarityNav.index;
    } else {
      this.variants = [data];
      this.viewIndex = 0;
    }
    this.renderVariant();
  }

  /** ◀/▶ 등급 이동 — 경계 클램프(범위 밖이면 무시). */
  private stepRarity(dir: number): void {
    const next = this.viewIndex + dir;
    if (next < 0 || next >= this.variants.length) return;
    this.viewIndex = next;
    this.renderVariant();
  }

  /** 현재 viewIndex variant를 패널에 렌더. 이름/등급/설명은 i18n 키로 매번 t() 재조회(언어 변경 안전). */
  private renderVariant(): void {
    const data = this.variants[this.viewIndex];
    if (!data) return;
    const color = RARITY_COLOR[data.rarity] ?? PALETTE.textPrimary;
    const colorNum = hexToNum(color);
    const name = t(data.nameKey ?? `module.${data.definitionId}.name`);
    this.title.setText(t("moduleInfo.title", { name, level: data.level })).setColor(color);
    this.rarityText.setText(rarityLabel(data.rarity));
    // 발동/크리티컬 확률 — pipeCrit가 있으면 chance 미기재 모듈도 발동확률 100%로 명시한다.
    if (!data.locked && data.pipeCritPercent !== undefined) {
      this.chanceText
        .setText(
          t("moduleInfo.chanceWithCrit", {
            chance: data.chancePercent ?? 100,
            crit: data.pipeCritPercent,
          }),
        )
        .setVisible(true);
    } else if (!data.locked && data.chancePercent !== undefined) {
      this.chanceText.setText(t("moduleInfo.chance", { percent: data.chancePercent })).setVisible(true);
    } else {
      this.chanceText.setVisible(false);
    }
    this.descText.setText(t(data.descKey ?? `module.${data.definitionId}.desc`));
    // 구체 스탯 — descText 실제 높이 아래에 한 줄씩(세로 나열). 라벨은 t(moduleStat.<key>)로 현지화.
    // 미발견(locked)은 수치를 ???로 가린다(스포일러 방지) — 이름/등급/모양 풀은 공개.
    const statRows = data.locked
      ? [{ key: "locked", text: t("moduleInfo.lockedStats"), color: PALETTE.textDim }]
      : data.stats
          .filter((s) => s.key !== "pipeCritChance")
          .map((s) => ({
            key: s.key,
            text: `${t(`moduleStat.${s.key}`)} ${s.value}`,
            color: this.statColor(s.key, color),
          }));
    this.renderStatRows(statRows, this.descText.y + this.descText.height + 8);
    // 새 모듈/등급 표시 → 확대 조각을 보유(대표) 조각으로 리셋(모양+footprint).
    this.activePiece = { shapeId: data.shapeId, footprintId: data.footprintId } as ModulePiece;
    this.drawMainIcon(data, colorNum);
    this.rebuildPoolRow(data, colorNum);
    this.updateRarityNav();
  }

  private statColor(key: string, rarityColor: string): string {
    return key.startsWith("pipeCrit") || key.startsWith("launcherCrit") || key === "critChance" || key === "critMul"
      ? MODULE_BRICK_BALANCE.critTextColor
      : rarityColor;
  }

  private ensureStatText(index: number): Phaser.GameObjects.Text {
    let row = this.statTexts[index];
    if (row) return row;
    row = this.scene.add
      .text(this.cx, 0, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.body,
        color: PALETTE.textBright,
        align: "center",
        wordWrap: { width: PANEL_W - 44 },
      })
      .setOrigin(0.5, 0);
    this.contentContainer.add(row);
    this.statTexts[index] = row;
    return row;
  }

  private renderStatRows(
    rows: readonly { readonly key: string; readonly text: string; readonly color: string }[],
    startY: number,
  ): void {
    let y = startY;
    rows.forEach((r, i) => {
      const row = this.ensureStatText(i);
      row.setText(r.text).setColor(r.color).setPosition(this.cx, y).setVisible(r.text.length > 0);
      y += row.height + 2;
    });
    for (let i = rows.length; i < this.statTexts.length; i++) this.statTexts[i]!.setVisible(false);
  }

  /** ◀/▶ 표시·활성 갱신 — variants 1개면 숨김, 양끝 경계면 흐리게(더 이동 불가 안내). */
  private updateRarityNav(): void {
    const multi = this.variants.length > 1;
    this.prevBtn.setVisible(multi);
    this.nextBtn.setVisible(multi);
    if (!multi) return;
    this.prevBtn.setAlpha(this.viewIndex > 0 ? 1 : 0.3);
    this.nextBtn.setAlpha(this.viewIndex < this.variants.length - 1 ? 1 : 0.3);
  }

  /** 메인 대표 아이콘 — 도감 cell/그리드 배치와 같은 기능 아이콘을 쓴다. */
  private drawMainIcon(data: ModuleInfoData, color: number): void {
    this.mainIcon
      .setTexture(data.iconKey ?? moduleCodexIconKey(data.definitionId))
      .setTint(color)
      .setAlpha(data.locked ? 0.55 : 1);
  }

  /** 모양 풀 행 — piecePool 각 조각을 **자기 footprint로** 미니 셀(bg + 포트선)로 가로 중앙 나열. 정적 표시(클릭 비활성화). */
  private rebuildPoolRow(data: ModuleInfoData, color: number): void {
    for (const o of this.poolEntries) o.destroy();
    this.poolEntries.length = 0;
    const pool = data.piecePool;
    if (pool.length === 0) return;
    const step = POOL_CELL_PX + POOL_GAP;
    const startX = this.cx - ((pool.length - 1) / 2) * step;
    pool.forEach((piece, i) => {
      const x = startX + i * step;
      const isActive =
        piece.shapeId === this.activePiece.shapeId && piece.footprintId === this.activePiece.footprintId;
      // 모양 풀은 가질 수 있는 모양을 정적으로 표시만 한다(클릭 비활성화 — setInteractive/pointerup 없음).
      const bg = this.scene.add
        .rectangle(x, this.poolRowY, POOL_CELL_PX, POOL_CELL_PX, PALETTE_NUM.cellBg)
        .setStrokeStyle(isActive ? 2 : 1, isActive ? color : PALETTE_NUM.cellStroke);
      // 각 조각의 자기 footprint를 반영 — drawRewardShapeIcon이 span 보정→placedShapeLayout→drawCellPorts를 통합.
      const g = drawRewardShapeIcon(this.scene, x, this.poolRowY, POOL_CELL_PX, piece, isActive ? color : PALETTE_NUM.portOff);
      this.contentContainer.add([bg, g]);
      this.poolEntries.push(bg, g);
    });
  }

  /** 언어 변경 — 정적 라벨 + 현재 variant 재렌더(제목/등급/설명을 definitionId로 재번역). 등급 미리보기 상태 유지. */
  applyLanguage(): void {
    this.poolLabel.setText(t("moduleInfo.shapePoolLabel"));
    if (this.variants.length === 0) return;
    this.renderVariant();
  }
}
